/**
 * useDeviceStateSync
 *
 * Polls Kasa device status and syncs state into viewer components
 * that have a deviceBinding configured. Runs at the viewer level,
 * not per-component.
 */

import { useEffect, useRef } from "react";
import { useViewerStore } from "@/stores/viewer/viewer-store";
import { useKasaStore } from "@/integrations/kasa/kasa-store";
import type { DeviceBinding } from "@/components/shared/rendering/InteractionContext";
import type { Component } from "@/types";

const POLL_INTERVAL_MS = 5000;

interface BoundComponent {
  componentId: string;
  componentType: string;
  binding: DeviceBinding;
}

/**
 * Find all components with a device binding.
 */
function findBoundComponents(components: Component[]): BoundComponent[] {
  const bound: BoundComponent[] = [];
  for (const component of components) {
    const action = (component.properties?.action as Record<string, unknown>) || {};
    const binding = action.deviceBinding as DeviceBinding | undefined;
    if (binding) {
      bound.push({
        componentId: component.id,
        componentType: component.type,
        binding,
      });
    }
  }
  return bound;
}

/**
 * Map device state to component property updates based on component type.
 */
function mapDeviceStateToProps(
  deviceStatus: string,
  brightness: number | undefined,
  componentType: string
): Record<string, unknown> | null {
  const isOn = deviceStatus === "on";

  switch (componentType) {
    case "PUSH_BUTTON":
    case "ICON_BUTTON":
      return { isActive: isOn };
    case "TOGGLE_SWITCH":
      return { isOn };
    case "SLIDER":
    case "RADIAL_DIAL":
    case "STEPPER":
      // Map brightness (0-100) to component value
      return { value: brightness ?? (isOn ? 100 : 0) };
    default:
      return null;
  }
}

export function useDeviceStateSync() {
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const sync = () => {
      const { components } = useViewerStore.getState();
      const boundComponents = findBoundComponents(components);
      if (boundComponents.length === 0) return;

      // Fetch latest device state
      useKasaStore.getState().fetchESP32Devices();

      // After a short delay for the fetch to complete, push state to components
      setTimeout(() => {
        const devices = useKasaStore.getState().esp32Devices;
        const updateComponent = useViewerStore.getState().updateComponent;

        for (const { componentId, componentType, binding } of boundComponents) {
          const device = devices.find((d) => d.deviceId === binding.deviceId);
          if (!device) continue;

          const propUpdates = mapDeviceStateToProps(
            device.status,
            device.brightness,
            componentType
          );
          if (!propUpdates) continue;

          const existing = useViewerStore.getState().getComponentById(componentId);
          if (existing) {
            updateComponent(componentId, {
              properties: { ...existing.properties, ...propUpdates },
            });
          }
        }
      }, 500);
    };

    // Initial sync
    sync();

    // Poll every 5 seconds
    pollRef.current = setInterval(sync, POLL_INTERVAL_MS);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);
}
